import { TFunction } from 'i18next';
import { AIActionKind, AIPromptActionTemplate } from 'models/copilot/actions/aiActionTemplate';
import { Iso27001Rooms } from 'models/projects/iso27001Rooms';

/**
 * What every ready-made question of the project scene is grounded on: the project (and the room, when
 * one is open) by name, so the agent starts on the right subject. The usage words the grounding from
 * its content; it is resolved with `t` when the menu or the panel reads the prompt, like the labels.
 */
export type ProjectQuestionParams = {
  getGrounding: (t: TFunction<string[]>) => string;
};

export type ProjectWhatNowParams = ProjectQuestionParams & {
  // undefined on the project surfaces (overview, milestone, step); the question then covers the project
  roomName?: string;
  projectName: string;
};

// The general question that fits every surface: the project as a whole, or the open room.
export const projectWhatNowTemplate: AIPromptActionTemplate<ProjectWhatNowParams> = {
  id: 'project.whatNow',
  kind: AIActionKind.Prompt,
  getLabel(t: TFunction<string[]>) {
    return t('copilot:Actions.Projects.WhatNow.Label');
  },
  getPrompt(t: TFunction<string[]>, params) {
    const question =
      params.roomName === undefined
        ? t('copilot:Actions.Projects.WhatNow.PromptOverview')
        : t('copilot:Actions.Projects.WhatNow.Prompt', {
            room: params.roomName,
            project: params.projectName,
          });

    return `${question} ${params.getGrounding(t)}`;
  },
};

export type ProjectOverviewAuditorParams = ProjectQuestionParams & {
  milestoneName: string;
};

export const projectOverviewAuditorTemplate: AIPromptActionTemplate<ProjectOverviewAuditorParams> = {
  id: 'project.overviewAuditor',
  kind: AIActionKind.Prompt,
  getLabel(t: TFunction<string[]>) {
    return t('copilot:Actions.Projects.OverviewAuditor.Label');
  },
  getPrompt(t: TFunction<string[]>, params) {
    const question = t('copilot:Actions.Projects.OverviewAuditor.Prompt', { milestone: params.milestoneName });

    return `${question} ${params.getGrounding(t)}`;
  },
};

// One room, one question: each room this standard owns has its own template, so every label and
// prompt is a literal translation key.
export const projectProfileAuditorTemplate: AIPromptActionTemplate<ProjectQuestionParams> = {
  id: 'project.profileAuditor',
  kind: AIActionKind.Prompt,
  getLabel(t: TFunction<string[]>) {
    return t('copilot:Actions.Projects.ProfileAuditor.Label');
  },
  getPrompt(t: TFunction<string[]>, params) {
    return `${t('copilot:Actions.Projects.ProfileAuditor.Prompt')} ${params.getGrounding(t)}`;
  },
};

export const projectScopeCheckTemplate: AIPromptActionTemplate<ProjectQuestionParams> = {
  id: 'project.scopeCheck',
  kind: AIActionKind.Prompt,
  getLabel(t: TFunction<string[]>) {
    return t('copilot:Actions.Projects.ScopeCheck.Label');
  },
  getPrompt(t: TFunction<string[]>, params) {
    return `${t('copilot:Actions.Projects.ScopeCheck.Prompt')} ${params.getGrounding(t)}`;
  },
};

export const projectObjectivesKeepOrArchiveTemplate: AIPromptActionTemplate<ProjectQuestionParams> = {
  id: 'project.objectivesKeepOrArchive',
  kind: AIActionKind.Prompt,
  getLabel(t: TFunction<string[]>) {
    return t('copilot:Actions.Projects.ObjectivesKeepOrArchive.Label');
  },
  getPrompt(t: TFunction<string[]>, params) {
    return `${t('copilot:Actions.Projects.ObjectivesKeepOrArchive.Prompt')} ${params.getGrounding(t)}`;
  },
};

export const projectRisksWhyHereTemplate: AIPromptActionTemplate<ProjectQuestionParams> = {
  id: 'project.risksWhyHere',
  kind: AIActionKind.Prompt,
  getLabel(t: TFunction<string[]>) {
    return t('copilot:Actions.Projects.RisksWhyHere.Label');
  },
  getPrompt(t: TFunction<string[]>, params) {
    return `${t('copilot:Actions.Projects.RisksWhyHere.Prompt')} ${params.getGrounding(t)}`;
  },
};

export const projectRolesMandatoryTemplate: AIPromptActionTemplate<ProjectQuestionParams> = {
  id: 'project.rolesMandatory',
  kind: AIActionKind.Prompt,
  getLabel(t: TFunction<string[]>) {
    return t('copilot:Actions.Projects.RolesMandatory.Label');
  },
  getPrompt(t: TFunction<string[]>, params) {
    return `${t('copilot:Actions.Projects.RolesMandatory.Prompt')} ${params.getGrounding(t)}`;
  },
};

export const projectDocumentsSummaryTemplate: AIPromptActionTemplate<ProjectQuestionParams> = {
  id: 'project.documentsSummary',
  kind: AIActionKind.Prompt,
  getLabel(t: TFunction<string[]>) {
    return t('copilot:Actions.Projects.DocumentsSummary.Label');
  },
  getPrompt(t: TFunction<string[]>, params) {
    return `${t('copilot:Actions.Projects.DocumentsSummary.Prompt')} ${params.getGrounding(t)}`;
  },
};

export type ProjectSummarizeRoomParams = ProjectQuestionParams & {
  roomKey: string;
};

// The four list rooms can be summarized; the rooms without a selectable list (profile, scope) cannot.
export const isProjectListRoom = (roomKey: string): boolean =>
  roomKey === Iso27001Rooms.Objectives ||
  roomKey === Iso27001Rooms.Risks ||
  roomKey === Iso27001Rooms.Roles ||
  roomKey === Iso27001Rooms.Documents;

// The summarize label of one list room: a switch over literal keys, so the scanner sees them.
const getProjectSummarizeLabel = (t: TFunction<string[]>, roomKey: string): string => {
  switch (roomKey) {
    case Iso27001Rooms.Objectives:
      return t('copilot:Actions.Projects.Summarize.objectives');
    case Iso27001Rooms.Risks:
      return t('copilot:Actions.Projects.Summarize.risks');
    case Iso27001Rooms.Roles:
      return t('copilot:Actions.Projects.Summarize.roles');
    case Iso27001Rooms.Documents:
      return t('copilot:Actions.Projects.Summarize.documents');
    default:
      return '';
  }
};

/**
 * Summarizes a list room in the panel, whole or by the rows the reader selected. The prompt words
 * the turn and the selection travels with the request, so the agent reads the rows.
 */
export const projectSummarizeRoomTemplate: AIPromptActionTemplate<ProjectSummarizeRoomParams> = {
  id: 'project.summarizeRoom',
  kind: AIActionKind.Prompt,
  acceptsSelection: true,
  getLabel(t: TFunction<string[]>, params) {
    return getProjectSummarizeLabel(t, params.roomKey);
  },
  getPrompt(t: TFunction<string[]>, params) {
    return `${getProjectSummarizeLabel(t, params.roomKey)} ${params.getGrounding(t)}`;
  },
};
